/**
 * Sniffs the first 12 bytes of a File to infer its extension from magic bytes.
 *
 * Returns the detected extension (without dot), or null if the format is
 * unrecognised. Only binary formats with reliable magic bytes are covered —
 * PDF, JPEG, PNG, HEIC, HEIF. Text-based formats (HTML, CSV, etc.) have no
 * magic bytes and cannot be reliably detected this way.
 */
export async function detectFileExtension(file: File): Promise<string | null> {
  const buffer = await file.slice(0, 12).arrayBuffer()
  const bytes = new Uint8Array(buffer)

  // PDF: %PDF (25 50 44 46)
  if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) return "pdf"

  // PNG (89 50 4E 47 0D 0A 1A 0A)
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return "png"

  // JPEG: starts FF D8
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return "jpg"

  // HEIC / HEIF: ISO Base Media File Format — 'ftyp' box sits at byte offset 4,
  // followed by a 4-byte brand that identifies the codec variant.
  if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
    const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]).toLowerCase()
    if (["heic", "heix", "hevc", "hevx"].includes(brand)) return "heic"
    if (["mif1", "msf1"].includes(brand)) return "heif"
  }

  return null
}
